import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  composerModelPickerOptions,
  resolveInitialSessionModel,
} from '#src/constants/document-composer-popular-models';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { useCurrentUser } from '#src/providers/user-provider';

/**
 * Plain-text marker for an Editor.js block the user emphasized ("add to chat").
 * Parsed server-side in ask-ai-react (document branch); must stay in sync with that regex.
 */
export function formatEmphasisToken(blockId: string): string {
  return `<emphasis:${blockId}>`;
}

export function hasComposerContent(promptText: string): boolean {
  return promptText.trim().length > 0;
}

export interface DocumentChatComposerContextValue {
  promptText: string;
  setPromptText: React.Dispatch<React.SetStateAction<string>>;
  /** Ephemeral OpenAI model id for the next document edit prompt (session-only). */
  sessionComposerModel: string;
  setSessionComposerModel: React.Dispatch<React.SetStateAction<string>>;
  /** Full deployment allowlist for the picker (popular ids first); used for search and selection. */
  composerModelOptions: string[];
  /** Appends an emphasis token for the given Editor.js block id, then you may call `focusComposer`. */
  appendEmphasis: (blockId: string) => void;
  clear: () => void;
  focusComposer: () => void;
  composerRootRef: React.RefObject<HTMLDivElement | null>;
}

const DocumentChatComposerContext = createContext<
  DocumentChatComposerContextValue | undefined
>(undefined);

export function DocumentChatComposerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const user = useCurrentUser(false);
  const { aiAssistEditChatModels, defaultAiAssistEditChatModel } =
    useAppSettings();

  const composerModelOptions = useMemo(
    () =>
      composerModelPickerOptions(
        aiAssistEditChatModels,
        defaultAiAssistEditChatModel,
      ),
    [aiAssistEditChatModels, defaultAiAssistEditChatModel],
  );

  const effectivePreferred =
    user?.effectiveAiAssistEditChatModel ?? defaultAiAssistEditChatModel;

  const [sessionComposerModel, setSessionComposerModel] = useState(() =>
    resolveInitialSessionModel(effectivePreferred, composerModelOptions),
  );

  const [promptText, setPromptText] = useState('');
  const composerRootRef = useRef<HTMLDivElement | null>(null);

  const appendEmphasis = useCallback((blockId: string) => {
    const token = formatEmphasisToken(blockId);
    setPromptText((prev) => {
      if (!prev.trim()) return token;
      const sep = prev.endsWith('\n') || prev.endsWith(' ') ? '' : ' ';
      return prev + sep + token;
    });
  }, []);

  const clear = useCallback(() => {
    setPromptText('');
  }, []);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      const root = composerRootRef.current;
      if (!root) return;
      const el = root.querySelector<HTMLTextAreaElement>(
        'textarea[data-chat-composer-text="true"]',
      );
      el?.focus();
    });
  }, []);

  const value = useMemo(
    () => ({
      promptText,
      setPromptText,
      sessionComposerModel,
      setSessionComposerModel,
      composerModelOptions,
      appendEmphasis,
      clear,
      focusComposer,
      composerRootRef,
    }),
    [
      promptText,
      sessionComposerModel,
      composerModelOptions,
      appendEmphasis,
      clear,
      focusComposer,
    ],
  );

  return (
    <DocumentChatComposerContext.Provider value={value}>
      {children}
    </DocumentChatComposerContext.Provider>
  );
}

export function useDocumentChatComposer() {
  const ctx = useContext(DocumentChatComposerContext);
  if (!ctx) {
    throw new Error(
      'useDocumentChatComposer must be used within DocumentChatComposerProvider',
    );
  }
  return ctx;
}
